import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { Action } from '../../lib/auth';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { 
  Flag,
  GitBranch,
  Layers,
  Plus,
  Loader2,
  CheckCircle,
  AlertCircle,
  Settings,
  FileText,
  ChevronRight,
  XCircle,
  Undo2,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import ScenarioModePanel from './ScenarioModePanel';

const ScheduleHeader = ({ 
  scheduleMode = 'normal', 
  onModeChange,
  selectedBaselineId,
  onBaselineChange,
  onBaselineDataLoad,
  selectedScenarioId,
  onScenarioChange,
  onScenarioDataLoad,
  onCreateBaseline,
  onCreateScenario,
  undoStack = [],
  onUndo,
  isUndoing = false,
  // New props for baseline columns in schedule view
  selectedBaselineForColumns = null,
  onBaselineForColumnsChange = null,
  onBaselineColumnsDataLoad = null
}) => {
  const { activeProject, activeSchedule, can } = useProject();
  // Creating baselines / scenarios is schedule-management work — gated by
  // MANAGE_SCHEDULES against the caller's role on the active project. Use the
  // PROJECT-bound can() (resolves against activeProjectRole from
  // /projects/{id}/members/me). NOTE: the GET /projects/{id} payload has NO role
  // field, so gating via useAuth().can with the project object's role always
  // passed undefined and wrongly denied every member — see the source guard
  // no-activeProject-role-gate.test.js.
  const canManageSchedules = can(Action.MANAGE_SCHEDULES);
  
  // Baselines & Scenarios lists
  const [baselines, setBaselines] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loadingBaselines, setLoadingBaselines] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [creatingFromTemplate, setCreatingFromTemplate] = useState(false);
  
  // Track if we've already auto-selected baseline for this schedule
  const hasAutoSelectedRef = useRef(false);
  const lastScheduleIdRef = useRef(null);
  
  // Create baseline dialog state
  const [showCreateBaselineDialog, setShowCreateBaselineDialog] = useState(false);
  const [creatingBaseline, setCreatingBaseline] = useState(false);
  const [newBaseline, setNewBaseline] = useState({
    name: '',
    description: '',
    baseline_type: 'secondary'
  });
  
  // Create scenario dialog state
  const [showCreateScenarioDialog, setShowCreateScenarioDialog] = useState(false);
  const [creatingScenario, setCreatingScenario] = useState(false);
  const [newScenario, setNewScenario] = useState({
    name: '',
    description: ''
  });
  
  // Scenario mode panel state
  const [showScenarioPanel, setShowScenarioPanel] = useState(false);
  
  // Reset auto-select flag when schedule changes
  useEffect(() => {
    if (activeSchedule?.id !== lastScheduleIdRef.current) {
      hasAutoSelectedRef.current = false;
      lastScheduleIdRef.current = activeSchedule?.id;
    }
  }, [activeSchedule?.id]);

  // Fetch baselines when schedule changes
  const fetchBaselines = useCallback(async () => {
    if (!activeSchedule?.id) return;
    setLoadingBaselines(true);
    try {
      const response = await api.get(`/baselines/schedule/${activeSchedule.id}`);
      setBaselines(response.data);
      
      // Auto-select first baseline for column display if:
      // 1. Baselines exist
      // 2. Haven't already auto-selected for this schedule
      // 3. Not in scenario mode
      // NEW: Instead of switching to baseline mode, just select baseline for columns in schedule view
      if (response.data.length > 0 && !hasAutoSelectedRef.current && scheduleMode !== 'scenario') {
        hasAutoSelectedRef.current = true;
        const firstBaseline = response.data[0];
        
        // Auto-select baseline for columns (without switching to baseline mode)
        onBaselineForColumnsChange?.(firstBaseline.id);
        
        // Load baseline data for column display
        try {
          const baselineDetailResponse = await api.get(`/baselines/${firstBaseline.id}`);
          onBaselineColumnsDataLoad?.(baselineDetailResponse.data);
        } catch (err) {
          console.error('Failed to auto-load baseline column data:', err);
        }
      }
    } catch (error) {
      console.error('Failed to fetch baselines:', error);
    } finally {
      setLoadingBaselines(false);
    }
  }, [activeSchedule?.id, scheduleMode, onBaselineForColumnsChange, onBaselineColumnsDataLoad]);

  // Fetch scenarios and templates when schedule changes
  const fetchScenarios = useCallback(async () => {
    if (!activeSchedule?.id) return;
    setLoadingScenarios(true);
    try {
      const [scenariosResponse, templatesResponse] = await Promise.all([
        api.get(`/scenarios/schedule/${activeSchedule.id}`),
        api.get('/scenarios/templates')
      ]);
      setScenarios(scenariosResponse.data.filter(s => s.status === 'draft'));
      setTemplates(templatesResponse.data);
    } catch (error) {
      console.error('Failed to fetch scenarios:', error);
    } finally {
      setLoadingScenarios(false);
    }
  }, [activeSchedule?.id]);

  useEffect(() => {
    if (activeSchedule?.id) {
      fetchBaselines();
      fetchScenarios();
    }
  }, [activeSchedule?.id, fetchBaselines, fetchScenarios]);

  // Handle baseline selection - load comparison data
  const handleBaselineSelect = async (baselineId) => {
    onBaselineChange?.(baselineId);
    
    if (baselineId && onBaselineDataLoad) {
      try {
        const response = await api.get(`/baselines/${baselineId}/compare`);
        onBaselineDataLoad(response.data);
      } catch (error) {
        console.error('Failed to load baseline comparison:', error);
        toast.error('Failed to load baseline comparison data');
      }
    } else if (!baselineId) {
      onBaselineDataLoad?.(null);
    }
  };

  // Handle baseline selection for columns (in schedule view, not baseline comparison mode)
  const handleBaselineForColumnsSelect = async (baselineId) => {
    onBaselineForColumnsChange?.(baselineId);
    
    if (baselineId && onBaselineColumnsDataLoad) {
      try {
        const response = await api.get(`/baselines/${baselineId}`);
        onBaselineColumnsDataLoad(response.data);
      } catch (error) {
        console.error('Failed to load baseline column data:', error);
        toast.error('Failed to load baseline data');
      }
    } else if (!baselineId) {
      onBaselineColumnsDataLoad?.(null);
    }
  };

  // Handle scenario selection - load scenario data or create from template
  const handleScenarioSelect = async (value) => {
    // Check if this is a template selection (prefixed with 'template:')
    if (value?.startsWith('template:')) {
      const templateId = value.replace('template:', '');
      const template = templates.find(t => t.id === templateId);
      if (!template || !activeSchedule?.id) return;
      
      setCreatingFromTemplate(true);
      try {
        // Create scenario instance from template
        const response = await api.post('/scenarios/from-template', {
          schedule_id: activeSchedule.id,
          template_id: templateId,
          name: template.name,
          description: template.description,
          parameters: template.parameters.reduce((acc, p) => ({ ...acc, [p.key]: p.default }), {})
        });
        
        // Refresh scenarios list
        await fetchScenarios();
        
        // Now select the newly created scenario
        const scenarioId = response.data.id;
        onScenarioChange?.(scenarioId);
        
        if (onScenarioDataLoad) {
          const [scenarioResponse, activitiesResponse] = await Promise.all([
            api.get(`/scenarios/${scenarioId}`),
            api.get(`/scenarios/${scenarioId}/activities`)
          ]);
          
          const currentActivitiesResponse = await api.get(`/activities/schedule/${activeSchedule.id}`);
          
          const scenarioData = {
            ...scenarioResponse.data,
            scenario_activities: activitiesResponse.data,
            current_activities: currentActivitiesResponse.data
          };
          
          onScenarioDataLoad(scenarioData);
        }
        
        toast.success(`Scenario "${template.name}" created`);
      } catch (error) {
        console.error('Failed to create scenario from template:', error);
        toast.error('Failed to create scenario from template');
      } finally {
        setCreatingFromTemplate(false);
      }
      return;
    }
    
    // Regular scenario selection
    const scenarioId = value;
    onScenarioChange?.(scenarioId);
    
    if (scenarioId && onScenarioDataLoad) {
      try {
        // Load scenario details and its activities
        const [scenarioResponse, activitiesResponse] = await Promise.all([
          api.get(`/scenarios/${scenarioId}`),
          api.get(`/scenarios/${scenarioId}/activities`)
        ]);
        
        // Also get current schedule activities for comparison
        const currentActivitiesResponse = await api.get(`/activities/schedule/${activeSchedule.id}`);
        
        const scenarioData = {
          ...scenarioResponse.data,
          scenario_activities: activitiesResponse.data,
          current_activities: currentActivitiesResponse.data
        };
        
        onScenarioDataLoad(scenarioData);
      } catch (error) {
        console.error('Failed to load scenario data:', error);
        toast.error('Failed to load scenario data');
      }
    } else if (!scenarioId) {
      onScenarioDataLoad?.(null);
    }
  };

  // Create baseline in-place
  const handleCreateBaseline = async (e) => {
    e.preventDefault();
    
    if (!newBaseline.name.trim()) {
      toast.error('Baseline name is required');
      return;
    }
    
    setCreatingBaseline(true);
    try {
      const payload = {
        schedule_id: activeSchedule.id,
        name: newBaseline.name.trim(),
        description: newBaseline.description?.trim() || null,
        baseline_type: newBaseline.baseline_type
      };
      
      const response = await api.post('/baselines', payload);
      toast.success('Baseline created successfully');
      
      await fetchBaselines();
      setShowCreateBaselineDialog(false);
      setNewBaseline({ name: '', description: '', baseline_type: 'secondary' });
      
      // Auto-select newly created baseline for columns (in schedule view)
      handleBaselineForColumnsSelect(response.data.id);
      
      onCreateBaseline?.(response.data);
      
    } catch (error) {
      console.error('Failed to create baseline:', error);
      const message = error.response?.data?.detail || 'Failed to create baseline';
      toast.error(message);
    } finally {
      setCreatingBaseline(false);
    }
  };

  // Create scenario in-place
  const handleCreateScenario = async (e) => {
    e.preventDefault();
    
    if (!newScenario.name.trim()) {
      toast.error('Scenario name is required');
      return;
    }
    
    setCreatingScenario(true);
    try {
      const payload = {
        schedule_id: activeSchedule.id,
        name: newScenario.name.trim(),
        description: newScenario.description?.trim() || null
      };
      
      const response = await api.post('/scenarios', payload);
      toast.success('Scenario created successfully');
      
      await fetchScenarios();
      setShowCreateScenarioDialog(false);
      setNewScenario({ name: '', description: '' });
      
      // Auto-select the newly created scenario
      handleScenarioSelect(response.data.id);
      onCreateScenario?.(response.data);
      
    } catch (error) {
      console.error('Failed to create scenario:', error);
      const message = error.response?.data?.detail || 'Failed to create scenario';
      toast.error(message);
    } finally {
      setCreatingScenario(false);
    }
  };

  const getModeIndicator = () => {
    if (scheduleMode === 'baseline') {
      const selectedBaseline = baselines.find(b => b.id === selectedBaselineId);
      return (
        <Badge className="bg-purple-100 text-purple-700 border-purple-200 gap-1 text-[10px]">
          <Flag className="h-3 w-3" />
          {selectedBaseline ? `Baseline: ${selectedBaseline.name}` : 'Baseline Mode'}
        </Badge>
      );
    }
    if (scheduleMode === 'scenario') {
      const selectedScenario = scenarios.find(s => s.id === selectedScenarioId);
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-200 gap-1 text-[10px]">
          <GitBranch className="h-3 w-3" />
          {selectedScenario ? (
            <>
              Scenario: {selectedScenario.name}
              {selectedScenario.template_name && (
                <span className="text-amber-500 ml-1">({selectedScenario.template_name})</span>
              )}
            </>
          ) : 'Scenario Mode'}
        </Badge>
      );
    }
    return null;
  };

  if (!activeProject) {
    return null;
  }

  return (
    <>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 shrink-0" data-testid="schedule-header">
        {/* Mode Toggles Row */}
        <div className="flex items-center gap-3">
          {/* Normal Mode Button */}
          <Button
            variant={scheduleMode === 'normal' ? 'secondary' : 'ghost'}
            size="sm"
            className={`h-7 px-3 text-xs gap-1.5 ${
              scheduleMode === 'normal' 
                ? 'bg-slate-200 text-slate-800 font-medium' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
            onClick={() => {
              onModeChange?.('normal');
              onBaselineDataLoad?.(null);
              onScenarioDataLoad?.(null);
            }}
            data-testid="mode-normal-button"
          >
            <Layers className="h-3.5 w-3.5" />
            Schedule
          </Button>
          
          {/* Baseline Selector (unified - replaces both baseline columns and baseline mode) */}
          {scheduleMode === 'normal' && (
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-50 rounded-md border border-purple-200">
                <Flag className="h-3.5 w-3.5 text-purple-500" />
                <span className="text-xs font-medium text-purple-700">Baseline:</span>
                <Select 
                  value={selectedBaselineForColumns || ''} 
                  onValueChange={handleBaselineForColumnsSelect}
                >
                  <SelectTrigger 
                    className="h-6 w-[150px] text-xs bg-white border-purple-200"
                    data-testid="baseline-selector"
                  >
                    <SelectValue placeholder="Select baseline..." />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingBaselines ? (
                      <div className="px-2 py-3 text-xs text-slate-400 text-center flex items-center justify-center gap-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading...
                      </div>
                    ) : baselines.length === 0 ? (
                      <div className="px-2 py-3 text-xs text-slate-400 text-center">
                        No baselines created yet
                      </div>
                    ) : (
                      baselines.map((baseline) => (
                        <SelectItem key={baseline.id} value={baseline.id} className="text-xs">
                          <div className="flex items-center gap-2">
                            <Flag className="h-3 w-3 text-purple-500" />
                            {baseline.name}
                            <span className="text-[10px] text-slate-400">
                              ({baseline.baseline_type})
                            </span>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Close Baseline Button - only visible when baseline is selected */}
              {selectedBaselineForColumns && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1 text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  onClick={() => {
                    onBaselineForColumnsChange?.(null);
                    onBaselineColumnsDataLoad?.(null);
                  }}
                  data-testid="close-baseline-button"
                >
                  <XCircle className="h-3.5 w-3.5" />
                  Close
                </Button>
              )}
              
              {/* New Baseline Button - always visible in Schedule mode for users who can manage schedules */}
              {canManageSchedules && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-xs gap-1 border-purple-200 text-purple-600 hover:bg-purple-50"
                  onClick={() => setShowCreateBaselineDialog(true)}
                  data-testid="new-baseline-button"
                >
                  <Plus className="h-3 w-3" />
                  New Baseline
                </Button>
              )}
            </div>
          )}
          
          {/* Scenario Mode Toggle */}
          <div className="flex items-center gap-1">
            <Button
              variant={scheduleMode === 'scenario' ? 'secondary' : 'ghost'}
              size="sm"
              className={`h-7 px-3 text-xs gap-1.5 rounded-r-none ${
                scheduleMode === 'scenario' 
                  ? 'bg-amber-100 text-amber-700 font-medium border border-amber-200' 
                  : 'text-slate-500 hover:text-amber-700 hover:bg-amber-50'
              }`}
              onClick={() => onModeChange?.(scheduleMode === 'scenario' ? 'normal' : 'scenario')}
              data-testid="mode-scenario-button"
            >
              <GitBranch className="h-3.5 w-3.5" />
              Scenario
            </Button>
            
            {scheduleMode === 'scenario' && (
              <Select 
                value={selectedScenarioId || ''} 
                onValueChange={handleScenarioSelect}
              >
                <SelectTrigger className="h-7 w-[180px] text-xs border-l-0 rounded-l-none bg-white" disabled={creatingFromTemplate}>
                  <SelectValue placeholder={creatingFromTemplate ? "Creating..." : "Select scenario..."} />
                </SelectTrigger>
                <SelectContent>
                  {loadingScenarios ? (
                    <div className="px-2 py-3 text-xs text-slate-400 text-center flex items-center justify-center gap-2">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Loading...
                    </div>
                  ) : (
                    <>
                      {/* Preset Scenarios (Templates) */}
                      {templates.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            Preset Scenarios
                          </div>
                          {templates.map((template) => (
                            <SelectItem key={`template:${template.id}`} value={`template:${template.id}`} className="text-xs">
                              <div className="flex items-center gap-2">
                                <FileText className="h-3 w-3 text-slate-400" />
                                {template.name}
                              </div>
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Separator */}
                      {templates.length > 0 && scenarios.length > 0 && (
                        <div className="my-1 border-t border-slate-200" />
                      )}
                      
                      {/* My Scenarios (Created) */}
                      {scenarios.length > 0 && (
                        <>
                          <div className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">
                            My Scenarios
                          </div>
                          {scenarios.map((scenario) => (
                            <SelectItem key={scenario.id} value={scenario.id} className="text-xs">
                              <div className="flex items-center gap-2">
                                <GitBranch className="h-3 w-3 text-amber-500" />
                                {scenario.name}
                              </div>
                            </SelectItem>
                          ))}
                        </>
                      )}
                      
                      {/* Empty state */}
                      {templates.length === 0 && scenarios.length === 0 && (
                        <div className="px-2 py-3 text-xs text-slate-400 text-center">
                          No scenarios available
                        </div>
                      )}
                    </>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {/* Mode Indicator */}
          {getModeIndicator()}
          
          {/* Session Undo Button - Always visible when there are changes to undo */}
          {undoStack && undoStack.length > 0 && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 text-xs gap-1.5 border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors"
                    onClick={onUndo}
                    disabled={isUndoing || !undoStack.length}
                    data-testid="global-undo-button"
                  >
                    {isUndoing ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Undo2 className="h-3.5 w-3.5" />
                    )}
                    Undo
                    <Badge className="ml-1 h-4 min-w-4 px-1 text-[10px] bg-blue-200 text-blue-800 border-0">
                      {undoStack.length}
                    </Badge>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs max-w-xs">
                  <p className="font-medium">Undo last change</p>
                  {undoStack[0] && (
                    <p className="text-slate-400 mt-0.5">
                      {undoStack[0].displayName}: "{undoStack[0].newValue}" → "{undoStack[0].oldValue}"
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          
          <div className="flex-1" />
          
          {/* Quick Actions - Create Scenario IN-PLACE */}
          {canManageSchedules && scheduleMode === 'scenario' && (
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs gap-1 border-amber-200 text-amber-600 hover:bg-amber-50"
                onClick={() => setShowCreateScenarioDialog(true)}
                data-testid="create-scenario-button"
              >
                <Plus className="h-3 w-3" />
                New
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 px-2 text-xs gap-1 border-amber-200 text-amber-600 hover:bg-amber-50"
                onClick={() => setShowScenarioPanel(true)}
                data-testid="scenario-panel-button"
              >
                <Settings className="h-3 w-3" />
                Templates & Settings
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create Baseline Dialog - IN-PLACE */}
      <Dialog open={showCreateBaselineDialog} onOpenChange={setShowCreateBaselineDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-purple-500" />
              Create Baseline
            </DialogTitle>
            <DialogDescription>
              Capture a snapshot of the current schedule for comparison.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateBaseline}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="baseline-name" className="text-sm font-medium">
                  Baseline Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="baseline-name"
                  placeholder="e.g., Initial Baseline, Rev 1"
                  value={newBaseline.name}
                  onChange={(e) => setNewBaseline({ ...newBaseline, name: e.target.value })}
                  className="h-9"
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="baseline-type" className="text-sm font-medium">
                  Baseline Type
                </Label>
                <Select 
                  value={newBaseline.baseline_type}
                  onValueChange={(value) => setNewBaseline({ ...newBaseline, baseline_type: value })}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="primary">Primary (Main Reference)</SelectItem>
                    <SelectItem value="secondary">Secondary</SelectItem>
                    <SelectItem value="what_if">What-If Analysis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="baseline-description" className="text-sm font-medium">
                  Description
                </Label>
                <Textarea
                  id="baseline-description"
                  placeholder="Optional description or notes..."
                  value={newBaseline.description}
                  onChange={(e) => setNewBaseline({ ...newBaseline, description: e.target.value })}
                  className="min-h-[80px] text-sm"
                />
              </div>
              
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                <div className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-purple-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-purple-700">
                    <p className="font-medium mb-1">This baseline will capture:</p>
                    <ul className="list-disc list-inside text-purple-600 space-y-0.5">
                      <li>All activities with current dates</li>
                      <li>Durations and relationships</li>
                      <li>CPM calculated values</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowCreateBaselineDialog(false)}
                disabled={creatingBaseline}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-purple-600 hover:bg-purple-700"
                disabled={creatingBaseline}
              >
                {creatingBaseline ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Flag className="mr-2 h-4 w-4" />
                    Create Baseline
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Scenario Dialog - IN-PLACE */}
      <Dialog open={showCreateScenarioDialog} onOpenChange={setShowCreateScenarioDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-amber-500" />
              Create What-If Scenario
            </DialogTitle>
            <DialogDescription>
              Create a copy of the current schedule to explore changes without affecting the live plan.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateScenario}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="scenario-name" className="text-sm font-medium">
                  Scenario Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="scenario-name"
                  placeholder="e.g., Accelerated Schedule, Resource Optimization"
                  value={newScenario.name}
                  onChange={(e) => setNewScenario({ ...newScenario, name: e.target.value })}
                  className="h-9"
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="scenario-description" className="text-sm font-medium">
                  Description
                </Label>
                <Textarea
                  id="scenario-description"
                  placeholder="Describe what changes you plan to explore..."
                  value={newScenario.description}
                  onChange={(e) => setNewScenario({ ...newScenario, description: e.target.value })}
                  className="min-h-[80px] text-sm"
                />
              </div>
              
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-amber-700">
                    <p className="font-medium mb-1">This scenario will:</p>
                    <ul className="list-disc list-inside text-amber-600 space-y-0.5">
                      <li>Create an isolated copy of all activities</li>
                      <li>Allow you to modify durations and dates</li>
                      <li>Run CPM to see forecast impact</li>
                      <li>Compare against the live schedule</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="flex gap-2">
                  <AlertCircle className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-500">
                    Changes to this scenario will not affect your live schedule until you explicitly apply them.
                  </p>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowCreateScenarioDialog(false)}
                disabled={creatingScenario}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-amber-600 hover:bg-amber-700"
                disabled={creatingScenario}
              >
                {creatingScenario ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <GitBranch className="mr-2 h-4 w-4" />
                    Create Scenario
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Scenario Mode Panel (Templates & Settings) */}
      <ScenarioModePanel
        isOpen={showScenarioPanel}
        onClose={() => setShowScenarioPanel(false)}
        selectedScenarioId={selectedScenarioId}
        onScenarioSelect={(id) => {
          handleScenarioSelect(id);
        }}
        onScenarioCreated={(scenario) => {
          fetchScenarios();
          onCreateScenario?.(scenario);
        }}
        onScenarioUpdated={() => {
          if (selectedScenarioId) {
            handleScenarioSelect(selectedScenarioId);
          }
        }}
      />
    </>
  );
};

export default ScheduleHeader;
